import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import Add from "@mui/icons-material/Add";
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useAsyncAction } from "nate-react-api-helpers";
import { useState } from "react";
import { api } from "../../../api/API";
import { Goal, Project } from "../../../api/Projects";
import { ConfirmDialog } from "../../../misc/ConfirmDialog";
import { useCurrentUser } from "../../../misc/CurrentUser";
import { useSnackbar } from "../../../misc/Snackbar";
import { ChainProjectPicker } from "./ChainProjectPicker";
import { ChainRowEditor } from "./ChainRowEditor";
import { DraftRow, newRow, removeRow, toChainRows } from "./chainRows";

// TaskChainDialog makes a list of tasks in one step, each waiting on the ones picked
// for it. Nothing is saved until "Create", and then all of it is saved or none.
export function TaskChainDialog(props: {
    onClose: () => void;

    // onCreated is handed the first task of the chain
    onCreated: (id: number) => void;
}) {
    const me = useCurrentUser();
    const snack = useSnackbar();
    const mine = me ? [me.id] : [];

    const [project, setProject] = useState<Project | null>(null);
    const [goal, setGoal] = useState<Goal | null>(null);
    const [rows, setRows] = useState<DraftRow[]>(() => [newRow(mine, true)]);
    const [focusKey, setFocusKey] = useState("");
    const [confirmClose, setConfirmClose] = useState(false);

    // the drag starts on a small move, so a click on the handle doesn't start one
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const count = rows.length;
    const hasWork = rows.some(row => row.title.trim() !== "");

    const create = useAsyncAction(async () => {
        if (!project) throw new Error("Pick a project");

        const result = await api.tasks.createChain({
            project: project.id,
            goal: goal ? goal.id : undefined,
            tasks: toChainRows(rows),
        });

        snack.success("Created " + result.ids.length + (result.ids.length === 1 ? " task" : " tasks"));
        props.onCreated(result.ids[0]);
    }, [project, goal, rows]);

    const addRow = () => {
        const row = newRow(mine, rows.length === 0);
        setRows(list => list.concat(row));
        setFocusKey(row.key);
    };

    const change = (key: string, changes: Partial<DraftRow>) =>
        setRows(list => list.map(row => row.key === key ? { ...row, ...changes } : row));

    const onDragEnd = (e: DragEndEvent) => {
        if (!e.over || e.active.id === e.over.id) return;

        setRows(list => {
            const from = list.findIndex(row => row.key === e.active.id);
            const to = list.findIndex(row => row.key === e.over?.id);
            return arrayMove(list, from, to);
        });
    };

    const close = () => {
        if (hasWork) setConfirmClose(true);
        else props.onClose();
    };

    return (
        <Dialog open onClose={close} maxWidth="md" fullWidth
                slotProps={{ paper: { sx: { height: "calc(100% - 64px)" } } }}>
            <DialogTitle>New task chain</DialogTitle>

            <DialogContent dividers>
                <ChainProjectPicker
                    project={project}
                    goal={goal}
                    onProject={value => {
                        setProject(value);

                        // the assignees of one team aren't on another, so each row starts
                        // over with the user
                        if (value && value.team !== project?.team) {
                            setRows(list => list.map(row => ({ ...row, assignedTo: mine })));
                        }
                    }}
                    onGoal={setGoal}
                />

                {!project && (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ pt: 1 }}>
                        Pick a project first. The tasks go to its team.
                    </Typography>
                )}

                <Box sx={{ pt: 2 }}>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                        <SortableContext items={rows.map(row => row.key)} strategy={verticalListSortingStrategy}>
                            {rows.map((row, index) => (
                                <ChainRowEditor
                                    key={row.key}
                                    rows={rows}
                                    index={index}
                                    team={project ? project.team : 0}
                                    autoFocus={row.key === focusKey}
                                    onChange={changes => change(row.key, changes)}
                                    onRemove={() => setRows(list => removeRow(list, row.key))}
                                    onEnter={addRow}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                </Box>

                <Button startIcon={<Add />} onClick={addRow} sx={{ mt: 1 }}>Add task</Button>
            </DialogContent>

            <DialogActions>
                <Box sx={{ flex: 1, px: 1 }}>{create.LoadingElement}</Box>
                <Button onClick={close}>Cancel</Button>
                <Button variant="contained" disabled={create.loading || !project || count === 0}
                        onClick={() => create.callback(null)}>
                    Create {count} {count === 1 ? "task" : "tasks"}
                </Button>
            </DialogActions>

            <ConfirmDialog
                open={confirmClose}
                title="Discard the chain?"
                message="The tasks you typed aren't saved yet."
                confirmLabel="Discard"
                confirmColor="error"
                onCancel={() => setConfirmClose(false)}
                onConfirm={() => {
                    setConfirmClose(false);
                    props.onClose();
                }}
            />
        </Dialog>
    );
}
